import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import axios from "axios";
import {
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import { getApiBase } from "./apiBase";
import { buildPluginsHeader, PLUGIN_IDS, isPluginEnabled } from "./plugins/registry";
import { canViewerPermission, getViewerEmailFromStorage } from "./plugins/userMatrixStorage";
import { useBoardTheme } from "./plugins/boardThemeStore";

ModuleRegistry.registerModules([AllCommunityModule]);

const AUTH_TOKEN_KEY = "projectBoardAuthToken";

function authHeaders() {
  const h = { ...buildPluginsHeader() };
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) h.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  return h;
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
}

function MailBodyTrigger(props) {
  const open = props.onPreview || props.context?.openBody;
  const disabled = typeof open !== "function";
  return (
    <button
      type="button"
      className="mail-log-body-icon-btn"
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) open(props.data);
      }}
      disabled={disabled}
      title="Preview email"
      aria-label="Preview email"
    >
      <svg
        className="mail-log-envelope-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    </button>
  );
}

/** Route wrapper: User Matrix plugin on + Administrator role only (matches sidebar). */
export function OutgoingMailLogGate() {
  const [rev, setRev] = useState(0);
  useEffect(() => {
    const bump = () => setRev((x) => x + 1);
    window.addEventListener("pb-plugins-changed", bump);
    window.addEventListener("pb-user-matrix-settings-changed", bump);
    return () => {
      window.removeEventListener("pb-plugins-changed", bump);
      window.removeEventListener("pb-user-matrix-settings-changed", bump);
    };
  }, []);
  void rev;
  const viewerEmail = getViewerEmailFromStorage();
  if (!isPluginEnabled(PLUGIN_IDS.USER_MATRIX) || !canViewerPermission(viewerEmail, "canViewMailLog", false)) {
    return <Navigate to="/" replace />;
  }
  return <OutgoingMailLogPage />;
}

export default function OutgoingMailLogPage() {
  const { agGridTheme } = useBoardTheme();
  const apiUrl = useMemo(() => getApiBase(), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);
  const [modalRow, setModalRow] = useState(null);
  const openBody = useCallback((row) => {
    if (!row) return;
    setModalRow(row);
  }, []);

  const closeModal = useCallback(() => setModalRow(null), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeModal]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(`${apiUrl}/email/status`, { headers: authHeaders() })
      .then((res) => {
        if (!cancelled) setStatus(res.data);
      })
      .catch((err) => {
        if (!cancelled && err.response?.status !== 403) {
          setStatus(null);
        }
      });
    axios
      .get(`${apiUrl}/outgoing-emails`, { headers: authHeaders() })
      .then((res) => {
        if (!cancelled) {
          setRows(Array.isArray(res.data) ? res.data : []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg =
            err.response?.data?.error ||
            (err.response?.status === 403
              ? "You do not have access to the outgoing mail log (server requires an admin account or PROJECT_BOARD_MAIL_LOG_EMAILS)."
              : err.message || "Could not load mail log.");
          setError(typeof msg === "string" ? msg : "Could not load mail log.");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  useEffect(() => {
    const id = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(id);
  }, [rows.length]);

  const columnDefs = useMemo(
    () => [
      {
        field: "sent_at",
        headerName: "Date / time",
        flex: 1,
        minWidth: 170,
        valueFormatter: (p) => formatWhen(p.value),
      },
      {
        field: "subject",
        headerName: "Subject",
        flex: 2,
        minWidth: 160,
      },
      {
        field: "to_emails",
        headerName: "To",
        flex: 2,
        minWidth: 180,
        valueGetter: (p) =>
          Array.isArray(p.data?.to_emails) ? p.data.to_emails.join(", ") : "",
      },
      {
        field: "cc_emails",
        headerName: "Cc",
        flex: 1,
        minWidth: 160,
        valueGetter: (p) =>
          Array.isArray(p.data?.cc_emails) ? p.data.cc_emails.join(", ") : "",
      },
      {
        colId: "body_preview",
        headerName: "Preview",
        width: 88,
        sortable: false,
        filter: false,
        cellRenderer: MailBodyTrigger,
        cellRendererParams: { onPreview: openBody },
      },
    ],
    [openBody]
  );

  const gridContext = useMemo(() => ({ openBody }), [openBody]);

  return (
    <div className="mail-log-page">
      <div className="mail-log-header">
        <h1 className="mail-log-title">Outgoing email log</h1>
        <p className="mail-log-lead">
          Messages sent by Project Board use{" "}
          <strong>{status?.mailFrom || "noreply@pboard.tech"}</strong> as the From address. SMTP is read from{" "}
          <code className="mail-log-code">backend/.env</code> on the API server. Every send (or skipped attempt) is
          recorded below.
        </p>
        {status ? (
          <div
            className={`mail-log-status-pill${status.outgoingEnabled ? " is-on" : ""}`}
            role="status"
          >
            Outgoing SMTP:{" "}
            <strong>{status.outgoingEnabled ? "enabled" : "not configured"}</strong>
            {!status.outgoingEnabled ? (
              <span className="mail-log-status-hint">
                {" "}
                — Set <code className="mail-log-code">SMTP_HOST</code>,{" "}
                <code className="mail-log-code">SMTP_USER</code>, and{" "}
                <code className="mail-log-code">SMTP_PASSWORD</code> in{" "}
                <code className="mail-log-code">backend/.env</code>, then restart the API container.
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="mail-log-error" role="alert">
          {error}
        </div>
      ) : null}

      {!error && loading ? <p className="mail-log-muted">Loading…</p> : null}

      {!error && !loading ? (
        <div className="mail-log-grid-wrap">
          <AgGridReact
            theme={agGridTheme}
            rowData={rows}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            context={gridContext}
            defaultColDef={{ sortable: true, filter: true, resizable: true }}
          />
          {rows.length === 0 ? (
            <p className="mail-log-muted mail-log-empty">No outgoing messages logged yet.</p>
          ) : null}
        </div>
      ) : null}

      <p className="mail-log-footer">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
      </p>

      {modalRow ? (
        <div className="mail-log-modal-backdrop" role="presentation" onClick={closeModal}>
          <div
            className="mail-log-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mail-log-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="mail-log-modal-title" className="mail-log-modal-title">
              {modalRow.subject || "(no subject)"}
            </h2>
            <p className="mail-log-modal-meta">
              <span>{formatWhen(modalRow.sent_at)}</span>
              {modalRow.send_status ? (
                <span className="mail-log-modal-status"> · {modalRow.send_status}</span>
              ) : null}
            </p>
            <p className="mail-log-modal-to">
              <strong>To:</strong>{" "}
              {Array.isArray(modalRow.to_emails) ? modalRow.to_emails.join(", ") : ""}
            </p>
            {Array.isArray(modalRow.cc_emails) && modalRow.cc_emails.length > 0 ? (
              <p className="mail-log-modal-to">
                <strong>Cc:</strong> {modalRow.cc_emails.join(", ")}
              </p>
            ) : null}
            {modalRow.error_message ? (
              <p className="mail-log-modal-err">
                <strong>Error:</strong> {modalRow.error_message}
              </p>
            ) : null}
            <pre className="mail-log-modal-body">
              {String(modalRow.body ?? "").trim() || "(No message body was recorded for this entry.)"}
            </pre>
            <button type="button" className="email-triggers-button mail-log-modal-close" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
